/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

/* eslint-disable no-param-reassign */

import type {
  STACColormapEntry,
  STACCubeVariable,
  STACIntervalColormap,
  STACRenderEntry,
  STACRasterBand,
} from '../api';
import { stacColorToRgba } from './stacColorUtils';

export interface STACLegendInterval {
  min: number;
  max: number;
  color: string;
  label?: string;
}

export interface STACContinuousLegend {
  type: 'continuous';
  min: number;
  max: number;
  colors: string[];
  showLabels?: boolean;
  stops?: { value: number; color: string; label?: string }[];
  unit?: string;
  title?: string;
}

export interface STACIntervalLegend {
  type: 'intervals';
  entries: STACLegendInterval[];
  unit?: string;
  title?: string;
}

export type STACLegendModel = STACContinuousLegend | STACIntervalLegend;

const toPhysicalValue = (raw: number, band?: STACRasterBand): number =>
  raw * (band?.scale ?? 1) + (band?.offset ?? 0);

/** Return known normalization bounds, leaving TIFF detection to OpenLayers otherwise. */
export const getStacGrayscaleRange = (
  renderEntry: STACRenderEntry,
  band?: STACRasterBand,
  variable?: STACCubeVariable,
): [number, number] | undefined => {
  if (renderEntry.rescale?.[0]) {
    return [renderEntry.rescale[0][0], renderEntry.rescale[0][1]];
  }
  if (
    band?.statistics?.minimum !== undefined &&
    band.statistics.maximum !== undefined
  ) {
    return [band.statistics.minimum, band.statistics.maximum];
  }
  if (!band && variable?.extent) {
    return variable.extent;
  }
  const integerRanges: Record<string, [number, number]> = {
    uint8: [0, 255],
    int8: [-128, 127],
    uint16: [0, 65535],
    int16: [-32768, 32767],
    uint32: [0, 4294967295],
    int32: [-2147483648, 2147483647],
  };
  const range = integerRanges[band?.data_type ?? ''];
  if (!range) {
    return undefined;
  }
  const [min, max] = range;
  const nodata = Number(renderEntry.nodata ?? band?.nodata);
  return [min === nodata ? min + 1 : min, max === nodata ? max - 1 : max];
};

/**
 * Returns the stored sample range used by GeoTIFF normalization. STAC band
 * statistics describe physical values when scale/offset are present, while
 * OpenLayers normalizes the encoded samples before applying the display style.
 */
export const getStacGrayscaleSampleRange = (
  renderEntry: STACRenderEntry,
  band?: STACRasterBand,
  variable?: STACCubeVariable,
): [number, number] | undefined => {
  if (renderEntry.rescale?.[0]) {
    return [renderEntry.rescale[0][0], renderEntry.rescale[0][1]];
  }

  if (
    band?.statistics?.minimum !== undefined &&
    band.statistics.maximum !== undefined
  ) {
    const scale = band.scale ?? 1;
    const offset = band.offset ?? 0;
    if (scale !== 0) {
      const samples = [band.statistics.minimum, band.statistics.maximum].map(
        (value) => (value - offset) / scale,
      );
      return [Math.min(...samples), Math.max(...samples)];
    }
  }

  return getStacGrayscaleRange(renderEntry, band, variable);
};

const getPaletteEntries = (
  renderEntry: STACRenderEntry,
  band?: STACRasterBand,
): [string, STACColormapEntry][] => {
  const { colormap } = renderEntry;
  if (!colormap || 'intervals' in colormap) {
    return [];
  }
  const nodata = renderEntry.nodata ?? band?.nodata;
  return Object.entries(colormap)
    .filter(
      ([raw, color]) =>
        Number.isFinite(Number(raw)) &&
        color[3] > 0 &&
        (nodata === undefined || Number(raw) !== Number(nodata)),
    )
    .sort(([first], [second]) => Number(first) - Number(second));
};

const isStacIntervalColormap = (
  colormap: STACRenderEntry['colormap'],
): colormap is STACIntervalColormap =>
  Boolean(colormap && 'intervals' in colormap);

export const makeStacLegendModel = (props: {
  renderEntry: STACRenderEntry;
  variable?: STACCubeVariable;
  band?: STACRasterBand;
}): STACLegendModel => {
  const { renderEntry, variable, band } = props;
  const title = renderEntry.title ?? variable?.description;
  const unit = band?.unit ?? variable?.unit;
  const style = renderEntry['geoweb:style'];

  if (style?.type === 'intervals' && style.entries.length) {
    return {
      type: 'intervals',
      entries: style.entries.map((entry) => ({
        ...entry,
        color: stacColorToRgba(entry.color),
      })),
      title,
      unit,
    };
  }
  if (style?.type === 'gradient' && style.entries.length) {
    const stops = style.entries
      .map((entry) => ({ ...entry, color: stacColorToRgba(entry.color) }))
      .sort((first, second) => first.value - second.value);
    return {
      type: 'continuous',
      min: stops[0].value,
      max: stops[stops.length - 1].value,
      colors: stops.map((stop) => stop.color),
      stops,
      title,
      unit,
    };
  }

  if (isStacIntervalColormap(renderEntry.colormap)) {
    return {
      type: 'intervals',
      entries: renderEntry.colormap.intervals.map(([range, color]) => ({
        min: Math.min(...range.map((raw) => toPhysicalValue(raw, band))),
        max: Math.max(...range.map((raw) => toPhysicalValue(raw, band))),
        color: stacColorToRgba(color),
      })),
      title,
      unit,
    };
  }

  const entries = getPaletteEntries(renderEntry, band);
  const grayscaleRange = getStacGrayscaleRange(renderEntry, band, variable);
  const rawRange =
    renderEntry.colormap && entries.length
      ? [Number(entries[0][0]), Number(entries[entries.length - 1][0])]
      : (grayscaleRange ?? [0, 255]);
  const grayscaleStatisticsArePhysical =
    !renderEntry.colormap &&
    !renderEntry.rescale?.[0] &&
    band?.statistics?.minimum !== undefined &&
    band.statistics.maximum !== undefined;
  let physicalRange: number[];
  if (renderEntry.colormap && !band && variable?.extent) {
    physicalRange = variable.extent;
  } else if (grayscaleStatisticsArePhysical) {
    physicalRange = rawRange;
  } else {
    physicalRange = rawRange.map((raw) => toPhysicalValue(raw, band));
  }
  const min = Math.min(...physicalRange);
  const max = Math.max(...physicalRange);
  const stops = entries
    .map(([raw, color]) => ({
      value:
        !band && variable?.extent
          ? physicalRange[0] +
            ((Number(raw) - rawRange[0]) / (rawRange[1] - rawRange[0] || 1)) *
              (physicalRange[1] - physicalRange[0])
          : toPhysicalValue(Number(raw), band),
      color: stacColorToRgba(color),
    }))
    .sort((first, second) => first.value - second.value);
  let colors = stops.map((stop) => stop.color);
  if (!renderEntry.colormap) {
    colors = ['rgba(0,0,0,1.0000)', 'rgba(255,255,255,1.0000)'];
  }
  if (!renderEntry.colormap && (band?.scale ?? 1) < 0) {
    colors = colors.slice().reverse();
  }

  return {
    type: 'continuous',
    min,
    max,
    colors,
    ...(stops.length > 0 && { stops }),
    ...(!renderEntry.colormap && !grayscaleRange && { showLabels: false }),
    title,
    unit,
  };
};

const formatLegendValue = (value: number): string =>
  Number.isInteger(value) ? value.toString() : value.toFixed(2);

const intervalText = (entry: STACLegendInterval, unit?: string): string => {
  const range = `${formatLegendValue(entry.min)} - ${formatLegendValue(entry.max)}${unit ? ` ${unit}` : ''}`;
  return entry.label ? `${entry.label} (${range})` : range;
};

const gradientLabels = (
  model: STACContinuousLegend,
): { value: number; text: string }[] => {
  if (model.showLabels === false) {
    return [];
  }
  const entries = model.stops?.filter(
    (stop) =>
      stop.label || stop.value === model.min || stop.value === model.max,
  ) ?? [{ value: model.min }, { value: model.max }];
  return entries.map((entry) => {
    const valueText = `${formatLegendValue(entry.value)}${model.unit ? ` ${model.unit}` : ''}`;
    return {
      value: entry.value,
      text:
        'label' in entry && entry.label
          ? `${entry.label} (${valueText})`
          : valueText,
    };
  });
};

export const getStacLegendWidth = (
  model: STACLegendModel,
  ctx: CanvasRenderingContext2D,
): number => {
  ctx.font = '11px Arial';
  const labels =
    model.type === 'intervals'
      ? model.entries.map((entry) => intervalText(entry, model.unit))
      : gradientLabels(model).map((entry) => entry.text);
  return Math.ceil(
    Math.max(
      150,
      ...labels.map((label) => ctx.measureText(label).width + 46),
      ctx.measureText(model.title ?? '').width + 20,
    ),
  );
};

export const renderStacLegend = (
  model: STACLegendModel,
  ctx: CanvasRenderingContext2D,
  opacity = 1,
): number => {
  const x = 10;
  const y = 10;
  const swatchSize = 16;
  const gap = 10;
  const rowSpacing = 4;

  ctx.globalAlpha = opacity;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.font = '11px Arial';
  ctx.fillStyle = '#000';

  let currentY = y;
  if (model.title) {
    ctx.fillText(model.title, x, currentY + swatchSize);
    currentY += swatchSize + rowSpacing;
  }

  if (model.type === 'intervals') {
    [...model.entries].reverse().forEach((entry) => {
      ctx.fillStyle = entry.color;
      ctx.fillRect(x, currentY, swatchSize, swatchSize);
      ctx.fillStyle = '#000';
      ctx.fillText(
        intervalText(entry, model.unit),
        x + swatchSize + gap,
        currentY + swatchSize - 3,
      );
      currentY += swatchSize + rowSpacing;
    });
  } else {
    const gradient = ctx.createLinearGradient(x, currentY + 180, x, currentY);
    const stops =
      model.stops ??
      model.colors.map((color, index) => ({
        color,
        value:
          model.min +
          (model.max - model.min) *
            (model.colors.length === 1 ? 0 : index / (model.colors.length - 1)),
      }));
    stops.forEach(({ color, value }) => {
      gradient.addColorStop(
        model.max === model.min
          ? 0
          : (value - model.min) / (model.max - model.min),
        color,
      );
    });
    ctx.fillStyle = gradient;
    ctx.fillRect(x, currentY, swatchSize, 180);
    ctx.fillStyle = '#000';
    gradientLabels(model).forEach(({ value, text }) => {
      const fraction =
        model.max === model.min
          ? 0
          : (value - model.min) / (model.max - model.min);
      ctx.fillText(
        text,
        x + swatchSize + gap,
        currentY + Math.max(12, 180 * (1 - fraction)),
      );
    });
    currentY += 180;
  }

  ctx.globalAlpha = 1;
  return currentY + 4;
};
